/**
 * Bridge Service - Direct HTTP uploads to local bridge server
 *
 * Uploads dice images directly to a local bridge server running on the user's PC,
 * bypassing AWS entirely. This enables local network storage for training data.
 */

import { createScopedReporter } from './errorReporter';

const SERVICE_TAG = '[BRIDGE]';
const reporter = createScopedReporter('BRIDGE');

/** Bridge server configuration */
export interface BridgeConfig {
  /** Bridge server URL (e.g., http://192.168.1.100:8080) */
  url: string;
}

/** Bridge upload response */
export interface BridgeUploadResponse {
  success: boolean;
  path: string;
  size: number;
}

/** Bridge health check response */
export interface BridgeHealthResponse {
  status: string;
  service: string;
  output_dir: string;
}

/**
 * Convert Blob to base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (data:image/jpeg;base64,)
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Bridge Service for local network uploads
 */
export const bridgeService = {
  /**
   * Test connection to bridge server
   *
   * @param url - Bridge server URL
   * @returns Test result with success status and message
   */
  async testConnection(url: string): Promise<{ success: boolean; message: string; latencyMs?: number }> {
    const startTime = Date.now();

    try {
      // Normalize URL
      const baseUrl = url.replace(/\/$/, '');

      console.info(`${SERVICE_TAG} Testing connection to ${baseUrl}`);

      const response = await fetch(`${baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      const latencyMs = Date.now() - startTime;

      if (!response.ok) {
        return {
          success: false,
          message: `Server returned ${response.status}: ${response.statusText}`
        };
      }

      const data: BridgeHealthResponse = await response.json();

      if (data.status === 'ok') {
        console.info(`${SERVICE_TAG} Connection successful`, { latencyMs, outputDir: data.output_dir });
        return {
          success: true,
          message: `Connected to bridge server. Output: ${data.output_dir}`,
          latencyMs
        };
      }

      return {
        success: false,
        message: 'Unexpected response from server'
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection failed';
      reporter.warning('Bridge connection test failed', {
        originalError: err instanceof Error ? err : undefined,
        showToast: false
      });

      // Provide helpful error messages
      if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        return {
          success: false,
          message: 'Cannot reach bridge server. Make sure it is running and you are on the same network.'
        };
      }

      return {
        success: false,
        message: `Connection failed: ${message}`
      };
    }
  },

  /**
   * Upload image to bridge server
   *
   * @param url - Bridge server URL
   * @param deviceId - Device identifier
   * @param diceType - Dice type (d4, d6, etc.)
   * @param faceNumber - Face number (1-based)
   * @param imageBlob - Image data as Blob
   * @param rotationIndex - Rotation variant index (0-based)
   * @returns Upload result
   */
  async uploadImage(
    url: string,
    deviceId: string,
    diceType: string,
    faceNumber: number,
    imageBlob: Blob,
    rotationIndex: number = 0
  ): Promise<BridgeUploadResponse> {
    const baseUrl = url.replace(/\/$/, '');

    console.info(`${SERVICE_TAG} Uploading to bridge`, {
      url: baseUrl,
      deviceId,
      diceType,
      faceNumber,
      rotationIndex,
      blobSize: imageBlob.size
    });

    const imageBase64 = await blobToBase64(imageBlob);

    const response = await fetch(`${baseUrl}/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        deviceId,
        diceType,
        faceNumber,
        rotationIndex,
        imageBase64
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Upload failed: ${response.status}`);
    }

    const data: BridgeUploadResponse = await response.json();

    console.info(`${SERVICE_TAG} Upload successful`, {
      path: data.path,
      size: data.size
    });

    return data;
  }
};
